import type {
  CgtOnSaleOutput,
  LossApplicationOutput,
  WashSaleOutput,
} from "./types.js";

// Plain-English result copy for the CGT surfaces. Lives in the rules package so
// every surface (Expo result screen, "my results" list, PDF report) renders
// identical wording from one source — that matters for regulation, not just DRY.
//
// Every string here is INFORMATION, not advice (validation §3). We NEVER say
// "sell now to save tax", "minimise/reduce your CGT", "you should hold past 12
// months", "harvest losses", or name a broker / trading platform / exchange to
// use. The CGT figure is always framed as an ESTIMATE ("the ATO finalises when
// you lodge"); the wash-sale read is always a NEUTRAL "the ATO rule would likely
// be concerned / not concerned" — never "you're safe to proceed" (no fixed time
// window means no honest output can promise safety). Pure string-builders,
// tested. Drift between channels is the information→advice kill-threat.

/** Whole-dollar AUD with thousands separators. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** Render a fraction as a whole-or-1dp percentage (e.g. 0.5 → "50%"). */
function pct(fraction: number): string {
  return `${+(fraction * 100).toFixed(1)}%`;
}

/** The mandatory information-not-advice disclaimer, identical on every CGT
 * surface. Note it explicitly disclaims a sell/hold/harvest recommendation AND
 * points to a registered tax agent — the panel-binding framing. */
export const DISCLAIMER =
  "Educational tool. Not financial advice. This explains how capital gains tax " +
  "works and gives an estimate based on the ATO rules for the financial year " +
  "shown — it is not advice to sell, hold, harvest losses, or re-buy, and not a " +
  "ruling on your situation. The ATO finalises your actual assessment when you " +
  "lodge; confirm your figures with the ATO or a registered tax agent.";

// ---------------------------------------------------------------------------
// cgtOnSale
// ---------------------------------------------------------------------------

export function cgtHeadline(out: CgtOnSaleOutput): string {
  if (out.capitalLoss > 0) {
    return `This sale is an estimated capital loss of ${formatAud(out.capitalLoss)}`;
  }
  if (out.grossCapitalGain === 0) {
    return "This sale produces no estimated capital gain or loss";
  }
  return `Estimated capital gains tax on this sale: ${formatAud(out.taxOnGain)}`;
}

/** How the gain and the tax were derived — descriptive, never a nudge. */
export function cgtBullet(out: CgtOnSaleOutput): string {
  if (out.capitalLoss > 0) {
    return `Your capital proceeds of ${formatAud(out.proceeds)} are below your reduced cost base of ${formatAud(out.reducedCostBase)}, so this sale is an estimated capital loss of ${formatAud(out.capitalLoss)}. A capital loss offsets capital gains only (never your salary or other income) and carries forward to future years until used.`;
  }
  if (out.grossCapitalGain === 0) {
    return `Your capital proceeds of ${formatAud(out.proceeds)} equal your cost base of ${formatAud(out.costBase)}, so there is no estimated capital gain or loss.`;
  }
  const discountClause = out.discountEligible
    ? `Because you held it for ${out.daysHeld} days (more than 12 months, excluding the days of acquisition and sale), the ${pct(out.discountRate)} CGT discount applies, so the net capital gain added to your income is ${formatAud(out.netCapitalGain)}.`
    : `Because you held it for ${out.daysHeld} days (12 months or less, excluding the days of acquisition and sale), the CGT discount does not apply, so the full gain of ${formatAud(out.netCapitalGain)} is added to your income.`;
  return `Your capital proceeds of ${formatAud(out.proceeds)} less your cost base of ${formatAud(out.costBase)} give a gross capital gain of ${formatAud(out.grossCapitalGain)}. ${discountClause} The net gain is taxed at your marginal rate on top of your other income, which is an estimated ${formatAud(out.taxOnGain)} in extra tax.`;
}

/** The 12-month timing fact — stated as the rule, never as "wait to save". */
export function twelveMonthBullet(out: CgtOnSaleOutput): string {
  return `The 12-month CGT-discount test runs from contract date to contract date (not settlement) and excludes both the day of acquisition and the day of the sale — the asset must be held for more than 12 months for the ${pct(0.5)} discount to apply.`;
}

/** Source-attribution line composed from the ruleset metadata. */
export function sourceBullet(ruleVersion: string, verifiedAt: string): string {
  return `These figures use the ATO capital gains tax rules and the resident marginal scale for the financial year shown (ruleset ${ruleVersion}), last verified ${verifiedAt}. The marginal scale reprices each financial year — always check ato.gov.au.`;
}

export function cgtExplanationBullets(
  out: CgtOnSaleOutput,
  ruleVersion: string,
  verifiedAt: string,
): string[] {
  return [
    cgtBullet(out),
    ...(out.grossCapitalGain > 0 ? [twelveMonthBullet(out)] : []),
    sourceBullet(ruleVersion, verifiedAt),
    DISCLAIMER,
  ];
}

// ---------------------------------------------------------------------------
// lossApplicationOrder
// ---------------------------------------------------------------------------

export function lossOrderHeadline(out: LossApplicationOutput): string {
  return `Estimated net capital gain after applying your capital losses: ${formatAud(out.netCapitalGain)}`;
}

export function lossOrderBullet(out: LossApplicationOutput): string {
  const carry = out.hasCarryForward
    ? ` You have ${formatAud(out.carryForwardToNextYear)} of unused capital losses left over, which carry forward to future years (they offset capital gains only, never your salary or other income).`
    : "";
  return `Following the ATO order, your capital losses of ${formatAud(out.totalLossesAvailable)} are subtracted from your gross gains BEFORE the 50% discount is applied, and against gains that do not qualify for the discount first. After that, your estimated net capital gain is ${formatAud(out.netCapitalGain)}.${carry}`;
}

// ---------------------------------------------------------------------------
// washSaleFlag — neutral "concerned / not concerned" framing only.
// ---------------------------------------------------------------------------

export function washSaleHeadline(out: WashSaleOutput): string {
  return out.risk === "elevated"
    ? "Based on what you've described, this pattern is one the ATO's wash-sale rule would likely be concerned with"
    : "Based on what you've described, this does not match the ATO's wash-sale pattern";
}

export function washSaleBullet(out: WashSaleOutput): string {
  if (out.risk === "elevated") {
    return (
      "Selling an asset at a loss and reacquiring the same or a substantially identical asset, where the dominant purpose is to crystallise the tax loss, is the wash-sale pattern the ATO can cancel under Part IVA (anti-avoidance ruling TA 2008/7). There is no fixed time window — no US-style 30-day rule — so no gap between selling and re-buying makes it safe; the test is your intention and whether your economic exposure really changed. This is information about the rule, not advice, and not a ruling on your situation — speak to a registered tax agent."
    );
  }
  return (
    "The wash-sale pattern needs a loss disposal, a reacquisition of the same or a substantially identical asset, and a dominant purpose of generating the tax loss. Based on what you've described, at least one of those is absent. This is information about the rule, not a clearance — the ATO applies it on the facts; speak to a registered tax agent if you're unsure."
  );
}
